// docs/72 IT15–IT16 — how any screen draws a piece: `ItemIcon` (its 24 px hero-kit icon, docs/129, on a
// rarity-coloured frame) and `ItemBody` (everything it does, in the words of core/items/describe.ts).
// The inventory, the hotbar, the Spoils, the village counter, trades, the founding market, the haul
// and the item book all use these, so a piece looks and reads the same everywhere.
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { catalogueItem, type CatalogueItem } from '../../core/items/catalogue';
import { describeItem, describeSet, setProgress, statLines, type AbilityWords, type SetHolding } from '../../core/items/describe';
import { lookOf, rankStats, ranksOf, upgradedName, upgradeTooltipLines, upgradeWords } from '../../core/items/upgrades';
import type { ItemStats } from '../../core/items/catalogue';
import { setOf } from '../../core/items/catalogue';
import { gradeWord, rarityOf, type Rarity } from '../../core/sim/gear';
import { itemDetails } from '../../core/sim/items';
import { dyedArtUrl } from '../../game/dyedArt';
import { ICON_SCALE, iconPx, itemIconUrl } from '../../game/itemIcon';
import { dyedLine } from '../../core/items/dyes';
import { loreOf } from './lore';
import { cardOf } from './useHeroGear';
import './items.css';

/** The rarity a piece is framed in: a catalogue piece by its rarity, a hand-authored one by its tier. */
export function itemRarity(def: number, tier: number, relic = false): Rarity {
  const c = catalogueItem(def);
  return rarityOf(c ? c.rarity : tier, relic || !!c?.relic);
}

/** The drifting motes an enchanted piece throws (docs/112 UP9), as a few spans the CSS animates. */
const MOTES = [0, 1, 2, 3];

/**
 * docs/112 UP9: what marks an upgraded piece wherever it is drawn, as an overlay over its tile (the
 * tile must be positioned): a faint glow breathing in its strongest enchantment's colour with a few
 * drifting motes, and a small badge in the corner for an improvement.
 */
export function UpgradeAura({ upgrades, small }: { upgrades?: readonly number[]; small?: boolean }) {
  const look = lookOf(upgrades);
  if (!look.count) return null;
  const style = look.glow ? ({ '--fk-glow': look.glow } as CSSProperties) : undefined;
  return (
    <span className={`fk-upaura ${look.enchanted ? 'is-enchanted' : ''} ${small ? 'is-small' : ''}`} style={style} aria-hidden data-testid="item-upgraded" data-enchanted={look.enchanted ? 'true' : 'false'} data-improved={look.improved ? 'true' : 'false'} data-upgrades={look.count}>
      {look.enchanted && !small && (
        <span className={`fk-upaura__motes fk-upaura__motes--${look.particles ?? 'motes'}`}>
          {MOTES.map((i) => <i key={i} />)}
        </span>
      )}
      {look.improved && <UpgradeBadge badge={look.badge ?? 'anvil'} />}
    </span>
  );
}

/** An icon (or anything) wrapped so `UpgradeAura` can sit over it. */
export function UpgradeMarks({ upgrades, children, small }: { upgrades?: readonly number[]; children: ReactNode; small?: boolean }) {
  if (!upgrades?.length) return <>{children}</>;
  return <span className="fk-upgraded">{children}<UpgradeAura upgrades={upgrades} small={small} /></span>;
}

/** An improvement's badge: a tiny pixel glyph in the corner (anvil, hammer, whetstone, needle, feather, rivet). */
const BADGE_PATHS: Record<string, string> = {
  anvil: 'M1 3h7v1h-1v1h-1v1h1v1H2V6h1V5H2V4H1z',
  hammer: 'M1 1h5v3H4v4H3V4H1z',
  whetstone: 'M1 5l4-4h2l-4 4v1h4v1H1z',
  needle: 'M6 1h1v1L2 7H1V6zM6 1h1',
  feather: 'M7 1C4 1 2 3 2 6l-1 1h1l1-1c3 0 4-2 4-5z',
  rivet: 'M3 2h2v1h1v2H5v1H3V5H2V3h1z',
};
export function UpgradeBadge({ badge }: { badge: string }) {
  return (
    <span className="fk-upaura__badge" data-badge={badge} data-testid="item-improved-badge" title="Improved by a craftsman">
      <svg viewBox="0 0 8 8" width="10" height="10" shapeRendering="crispEdges"><path d={BADGE_PATHS[badge] ?? BADGE_PATHS['anvil']} /></svg>
    </span>
  );
}

/** A piece's 24 px hero-kit icon (docs/129 §7.1, `game/itemIcon.ts`), on a frame the colour of its rarity
 *  (`framed`), or bare; an upgraded piece (docs/112) glows and wears its badge. */
export function ItemIcon({ def, tier = 1, name = '', relic, scale = 2, framed, title, className = '', upgrades, dye }: { def: number; tier?: number; name?: string; relic?: boolean; scale?: 1 | 2 | 3 | 4; framed?: boolean; title?: string; className?: string; upgrades?: readonly number[]; dye?: number }) {
  const icon = <ItemArtIcon def={def} name={name} scale={scale} title={title} dye={dye} />;
  if (!framed) {
    const bare = className ? <span className={className}>{icon}</span> : icon;
    return <UpgradeMarks upgrades={upgrades} small={scale === 1}>{bare}</UpgradeMarks>;
  }
  const framedIcon = <span className={`fk-item-icon fk-inv__tile--${itemRarity(def, tier, relic)} fk-item-icon--s${scale} ${className}`} data-def={def}>{icon}</span>;
  return <UpgradeMarks upgrades={upgrades} small={scale === 1}>{framedIcon}</UpgradeMarks>;
}

/**
 * Just the icon: the 24 px kit art at the largest whole scale that fits a frame sized for a 16 px sprite
 * at `scale` (24, 24, 48, 72 px), drawn pixel for pixel. A piece with no icon (no catalogue piece, no
 * stand-in, nothing in its name) draws `glyph` (default a diamond) in the same box. A dyed piece (docs/149)
 * draws its worn art in its dye instead, as the body wears it.
 */
export function ItemArtIcon({ def, name = '', scale = 2, title, glyph, dye }: { def: number; name?: string; scale?: 1 | 2 | 3 | 4; title?: string; glyph?: ReactNode; dye?: number }) {
  const [dyed, setDyed] = useState<string>();
  useEffect(() => {
    let live = true;
    setDyed(undefined);
    if (dye) void dyedArtUrl(def, dye).then((u) => live && setDyed(u));
    return () => { live = false; };
  }, [def, dye]);
  const url = dyed ?? itemIconUrl(def, name);
  const px = iconPx(scale);
  const label = { role: title ? 'img' : undefined, 'aria-label': title, 'aria-hidden': title ? undefined : true, title } as const;
  if (!url) return <span className="fk-sprite fk-sprite--glyph" data-item-glyph style={{ width: px, height: px, fontSize: 12 * ICON_SCALE[scale] }} {...label}>{glyph ?? '◆'}</span>;
  return <span className="fk-sprite" data-qa-art data-item-icon={def} style={{ width: px, height: px, backgroundImage: `url(${url})`, backgroundSize: '100% 100%', imageRendering: 'pixelated' }} {...label} />;
}

/** One line for a `title` tooltip: "Emberwake Longsword · Rare · Sword · Main hand — 7 weapon damage; …";
 *  an upgraded piece (docs/112) is "… +2" and lists what each upgrade adds. */
export function itemTooltip(def: number, tier: number, name: string, relic = false, upgrades?: readonly number[], dye = 0): string {
  const c = catalogueItem(def);
  const ups = upgradeTooltipLines(upgrades);
  const dyed = dyedLine(def, dye);
  const upText = `${ups.length ? `\n${ups.join('\n')}` : ''}${dyed ? `\n${dyed}` : ''}`;
  if (!c) {
    // a hand-authored piece reads from the sim's own card and its line of lore, as the inventory does
    const card = cardOf(def, tier);
    const d = card ? itemDetails(card) : undefined;
    const lore = loreOf(def);
    return `${upgradedName(name, upgrades)} · ${gradeWord(def, tier, relic)}${d ? ` · ${d.kind}` : ''}${d?.effects.length ? `\n${d.effects.join('\n')}` : ''}${upText}${lore ? `\n“${lore}”` : ''}`;
  }
  const d = describeItem(c);
  const worn = upgrades?.length ? statLines(upgradedStats(c.stats, upgrades, isWeaponFamily(c.family)), c.family) : d.worn;
  const lines = [...worn, ...d.powers, ...(d.active ? [`${d.active.name}: ${d.active.effects.join('; ')}`] : []), ...(d.use?.when === 'fight' ? [`Use: ${d.use.ability.effects.join('; ')}`] : d.use ? [`${d.use.verb}: ${d.use.line}`] : [])];
  const set = setOf(def);
  return `${upgradedName(name || c.name, upgrades)} · ${gradeWord(def, tier, relic)} · ${d.kind}${lines.length ? `\n${lines.join('\n')}` : ''}${upText}\n“${c.flavour}”${set ? `\nPart of the ${set.name} set` : ''}`;
}

const WEAPON_FAMILIES = ['sword', 'axe', 'mace', 'dagger', 'spear', 'bow', 'crossbow', 'staff', 'wand'];
const isWeaponFamily = (f: string): boolean => WEAPON_FAMILIES.includes(f);

/** docs/112 UP4: a piece's worn numbers with every upgrade's at its rank added (weapon damage,
 *  range and pierce only on a weapon, as `derive_stats` adds them from the main hand). */
export function upgradedStats(base: ItemStats | undefined, upgrades: readonly number[] | undefined, weapon: boolean): ItemStats {
  const out: ItemStats = { ...(base ?? {}) };
  for (const r of ranksOf(upgrades)) {
    for (const [k, v] of Object.entries(rankStats(r.up, r.rank))) {
      if (!weapon && ['base_dmg', 'range', 'pierce'].includes(k)) continue;
      if (k === 'range' && !(base?.range)) continue;
      out[k as keyof ItemStats] = (out[k as keyof ItemStats] ?? 0) + (v ?? 0);
    }
  }
  return out;
}

/** docs/112 UP9: the card's list of what is laid on a piece, each upgrade at its rank with what it adds. */
export function UpgradeList({ upgrades, compact }: { upgrades?: readonly number[]; compact?: boolean }) {
  const ws = upgradeWords(upgrades);
  if (!ws.length) return null;
  const look = lookOf(upgrades);
  return (
    <div className="fk-item-ups" data-testid="item-upgrades" style={look.glow ? ({ '--fk-glow': look.glow } as CSSProperties) : undefined}>
      <div className="fk-item-ups__head"><span className="fk-item-body__tag">Upgrades</span><span className="fk-faint">+{upgrades?.length ?? 0} laid on</span></div>
      <ul className="fk-item-ups__list">
        {ws.map((w) => (
          <li key={w.name} className={`fk-item-ups__up is-${w.kind}`} data-testid="item-upgrade" data-kind={w.kind}>
            <b>{w.kind === 'enchant' ? '✦' : '⚒'} {w.name}</b>
            {!compact && <span className="fk-item-ups__kind">{w.kind === 'enchant' ? 'enchantment' : 'improvement'}</span>}
            <span className="fk-item-ups__lines">{[...w.lines, ...(w.ability ? [`${w.ability.name}: ${w.ability.effects.join('; ')} (${w.ability.head})`] : [])].join(' · ')}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Ability({ label, a, testId }: { label: string; a: AbilityWords; testId: string }) {
  return (
    <div className="fk-item-body__ability" data-testid={testId}>
      <div className="fk-item-body__ability-head"><span className="fk-item-body__tag">{label}</span><b>{a.name}</b></div>
      <div className="fk-item-body__ability-line">{a.head}</div>
      <ul className="fk-inv__effects">{a.effects.map((e, i) => <li key={i}>{e}</li>)}</ul>
    </div>
  );
}

/** What a hand-authored piece (below the catalogue) does, as a card body: its sim card's numbers and its lore. */
export function GearBody({ def, tier, relic, upgrades }: { def: number; tier: number; relic?: boolean; upgrades?: readonly number[] }) {
  const card = cardOf(def, tier);
  const d = card ? itemDetails(card) : undefined;
  const lore = loreOf(def);
  return (
    <div className="fk-item-body fk-item-body--compact" data-testid="item-body" data-def={def}>
      {d ? (
        <ul className="fk-inv__effects" data-testid="inv-effects">{d.effects.length ? d.effects.map((e) => <li key={e}>{e}</li>) : <li className="fk-faint">No bonuses of its own.</li>}</ul>
      ) : relic ? <p className="fk-faint">A relic of this World's story. Its worth is in what it means.</p> : <p className="fk-faint">The details are still loading.</p>}
      <UpgradeList upgrades={upgrades} compact />
      {lore && <p className="fk-inv__lore">“{lore}”</p>}
    </div>
  );
}

/** What a hero holds of a piece's set, and whether this very piece is worn (the inventory knows). */
export interface SetOwner extends SetHolding { equipped: boolean }

/**
 * docs/110 SE4: the foot of a set piece's card, in italics: "Part of the … set", what the whole set
 * grants and which pieces it takes; then, for a piece in a hero's inventory, "n / N items collected",
 * and under it for a worn piece "Equip all items in this set to receive the bonus". A worn piece of a
 * set worn whole says only "Full set equipped", in bold green.
 */
export function SetFooter({ def, owner, compact }: { def: number; owner?: SetOwner; compact?: boolean }) {
  const set = setOf(def);
  if (!set) return null;
  const w = describeSet(set);
  const p = owner ? setProgress(def, owner) : undefined;
  const full = !!p?.complete && !!owner?.equipped;
  return (
    <div className={`fk-item-set ${full ? 'is-full' : ''}`} data-testid="item-set" data-set={set.id} data-full={full ? 'true' : 'false'}>
      <p className="fk-item-set__part" data-testid="item-set-part"><i>Part of the <b>{set.name}</b> set</i></p>
      <div className="fk-item-set__bonus" data-testid="item-set-bonus">
        <i className="fk-item-set__head">Full set bonus ({w.pieces.length} pieces):</i>
        <ul className="fk-item-set__lines">
          {w.bonus.map((l) => <li key={l}><i>{l}</i></li>)}
          <li className="fk-item-set__ability"><i><b>{w.ability.name}</b> ({w.ability.head}): {w.ability.effects.join('; ')}</i></li>
        </ul>
        {!compact && (
          <ul className="fk-item-set__pieces" aria-label={`${set.name} pieces`}>
            {w.pieces.map((x) => {
              const has = owner?.owned.has(x.def);
              return <li key={x.def} className={`${has ? 'is-owned' : ''} ${x.def === def ? 'is-this' : ''}`} data-testid="item-set-piece" data-owned={has ? 'true' : 'false'}><ItemIcon def={x.def} scale={1} /><i>{x.name}</i></li>;
            })}
          </ul>
        )}
      </div>
      {full ? (
        <p className="fk-item-set__full" data-testid="item-set-full"><b>Full set equipped</b></p>
      ) : p ? (
        <>
          <p className="fk-item-set__count" data-testid="item-set-count"><i>{p.owned} / {p.total} items collected</i></p>
          {owner?.equipped && <p className="fk-item-set__note" data-testid="item-set-note"><i>Equip all items in this set to receive the bonus</i></p>}
        </>
      ) : null}
    </div>
  );
}

/** Everything a catalogue piece does, as a card body: numbers, powers, active, use, flavour, source. */
export function ItemBody({ item, relicName, relicLore, compact, children, owner, upgrades }: { item: CatalogueItem; relicName?: string; relicLore?: string; compact?: boolean; children?: ReactNode; owner?: SetOwner; upgrades?: readonly number[] }) {
  const d = describeItem(item);
  // docs/112 UP9: the numbers include the upgrades; a line they changed is marked
  const base = new Set(d.worn);
  if (upgrades?.length) d.worn = statLines(upgradedStats(item.stats, upgrades, isWeaponFamily(item.family)), item.family);
  const marked = new Set(upgrades?.length ? d.worn.filter((l) => !base.has(l)) : []);
  return (
    <div className={`fk-item-body ${compact ? 'fk-item-body--compact' : ''}`} data-testid="item-body" data-def={item.id}>
      {item.relic && relicName && <div className="fk-item-body__relic">Forged by the storyteller from <i>{item.name}</i></div>}
      {(d.worn.length > 0 || d.powers.length > 0) && (
        <ul className="fk-inv__effects" data-testid="inv-effects">
          {d.worn.map((e) => <li key={e} className={`fk-item-body__worn ${marked.has(e) ? 'is-upgraded' : ''}`}>{e}{marked.has(e) && <span className="fk-item-body__upmark" title="with its upgrades"> ✦</span>}</li>)}
          {d.powers.map((e) => <li key={e} className="fk-item-body__power">{e}</li>)}
        </ul>
      )}
      {d.active && <Ability label="Active" a={d.active} testId="item-active" />}
      {d.use?.when === 'fight' && <Ability label="Use in a fight" a={d.use.ability} testId="item-use" />}
      <UpgradeList upgrades={upgrades} compact={compact} />
      {d.use?.when === 'camp' && (
        <div className="fk-item-body__ability" data-testid="item-camp">
          <div className="fk-item-body__ability-head"><span className="fk-item-body__tag">{d.use.verb} in camp</span></div>
          <div>{d.use.line}</div>
        </div>
      )}
      {children}
      <p className="fk-inv__lore">“{relicLore ?? d.flavour}”</p>
      {!compact && <p className="fk-item-body__source" data-testid="item-source">{d.classes}{d.price ? ` · ${d.price} gold` : ''} · {d.source}</p>}
      <SetFooter def={item.id} owner={owner} compact={compact} />
    </div>
  );
}
